// WB-INK-001/002/003/004 — pause detection → sealed stroke group → background recognition.
// Manual mode: suggestion chip (ink untouched until accepted). Auto mode: silent high-confidence conversion,
// brief flash on the new text, standard Undo restores the ink. Recognition never blocks drawing.
// Grouping: the idle timer only runs while the pen is up; a stroke that continues an in-flight group
// (next letter / i-dot / t-cross / next word on the same line) cancels that request and re-groups,
// while strokes elsewhere start an independent group. Text is placed on the ink's own baseline/size.
import { useCallback, useEffect, useRef, useState } from 'react';
import { apiRequest } from '../../utils/adminApi';
import { INK_IDLE_MS, boxContains, boxesIntersect, pickCandidates, strokeBox, strokeContinuesGroup } from './inkSegmentation';
import { fitTextToInk } from './inkFit';

import { renderGroupPng } from './inkRender';

const CLIENT_TIMEOUT_MS = 12000;
const AUTO_CONVERT_CONFIDENCE = 'high';
const FLASH_MS = 1400;
const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now());

const recordMetric = (m) => {
  if (typeof window === 'undefined') return;
  (window.__wbInkMetrics = window.__wbInkMetrics || []).push(m);
  apiRequest('/training/whiteboard/ink-metrics', { method: 'POST', body: JSON.stringify(m) }).catch(() => {});
};

export const useInkRecognition = ({ items, tool, enabled, autoConvert, history, textSize, textFont }) => {
  const [suggestion, setSuggestion] = useState(null); // { groupId, text, confidence, strokes, erases, color, box }
  const [flash, setFlash] = useState(null); // { id } of a freshly converted text item
  const [busy, setBusy] = useState(0);
  const burstRef = useRef([]);
  const lastStrokeAtRef = useRef(0);
  const timerRef = useRef(null);
  const pendingRef = useRef(new Map()); // groupId -> { ctrl, strokes, box }
  const itemsRef = useRef(items);
  itemsRef.current = items;
  const autoRef = useRef(autoConvert);
  autoRef.current = autoConvert;
  const prefsRef = useRef({ textSize, textFont });
  prefsRef.current = { textSize, textFont };

  const clearTimer = () => { clearTimeout(timerRef.current); timerRef.current = null; };
  const abortAll = () => { pendingRef.current.forEach((p) => p.ctrl.abort()); pendingRef.current.clear(); };

  const reset = useCallback(() => {
    clearTimer();
    abortAll();
    burstRef.current = [];
    setSuggestion(null);
    setBusy(0);
  }, []);

  // Swaps the group's strokes (and eraser marks fully inside it) for one text item — a single undoable step.
  const convert = useCallback((group, text) => {
    const all = itemsRef.current;
    const strokeIdx = group.strokes.map((it) => all.indexOf(it)).filter((i) => i >= 0);
    if (strokeIdx.length !== group.strokes.length) return null;
    const eraseIdx = group.erases.filter((e) => boxContains(group.box, strokeBox(e), 4)).map((e) => all.indexOf(e)).filter((i) => i >= 0);
    // WB-TEXT-FMT-001: the trainer's chosen size/style wins (captured when the word was written); ink only decides WHERE.
    const { textSize: size, textFont: font } = group.prefs || prefsRef.current;
    const fit = fitTextToInk(text, group.box, { size, font });
    const item = { type: 'text', id: newId(), x: fit.x, y: fit.y, text, color: group.color, size, font, source: 'ink' };
    history.replace([...strokeIdx, ...eraseIdx], item);
    return item;
  }, [history]);

  const recognize = useCallback(async (cand, strokeEndedAt) => {
    const all = itemsRef.current;
    const minIdx = Math.min(...cand.strokes.map((s) => all.indexOf(s)).filter((i) => i >= 0));
    const erases = all.filter((it, i) => it.type === 'erase' && i > minIdx && boxesIntersect(strokeBox(it), cand.box));
    const png = renderGroupPng(cand.strokes, erases, cand.box);
    const group = { groupId: newId(), strokes: cand.strokes, erases, box: cand.box, color: cand.strokes[0].color, prefs: { ...prefsRef.current } };
    const ctrl = new AbortController();
    pendingRef.current.set(group.groupId, { ctrl, strokes: cand.strokes, box: cand.box });
    const timeout = setTimeout(() => ctrl.abort(), CLIENT_TIMEOUT_MS);
    const tSent = now();
    setBusy((n) => n + 1);
    try {
      const res = await apiRequest('/training/whiteboard/recognize', {
        method: 'POST',
        signal: ctrl.signal,
        body: JSON.stringify({ image_base64: png.base64, group_id: group.groupId, stroke_count: cand.strokes.length, width: png.width, height: png.height }),
      });
      const tResp = now();
      if (!pendingRef.current.has(group.groupId)) return; // cancelled (re-grouped / tool change / disabled)
      const text = (res.text || '').trim();
      if (!res.is_text || !text) return;
      if (autoRef.current) {
        if (res.confidence !== AUTO_CONVERT_CONFIDENCE) return; // below threshold → ink stays untouched, silently
        const item = convert(group, text);
        if (!item) return;
        setFlash({ id: item.id });
        setTimeout(() => setFlash((f) => (f?.id === item.id ? null : f)), FLASH_MS);
        requestAnimationFrame(() => recordMetric({
          group_id: group.groupId, mode: 'auto', idle_ms: Math.round(tSent - strokeEndedAt), request_ms: Math.round(tResp - tSent),
          render_ms: Math.round(now() - tResp), total_ms: Math.round(now() - strokeEndedAt), model_latency_ms: res.latency_ms,
        }));
        return;
      }
      recordMetric({ group_id: group.groupId, mode: 'manual', idle_ms: Math.round(tSent - strokeEndedAt), request_ms: Math.round(tResp - tSent), render_ms: 0, total_ms: Math.round(tResp - strokeEndedAt), model_latency_ms: res.latency_ms });
      setSuggestion({ ...group, text, confidence: res.confidence });
    } catch (e) {
      if (!ctrl.signal.aborted) console.debug('[wb-ink] recognition failed — handwriting kept', e?.message);
    } finally {
      clearTimeout(timeout);
      pendingRef.current.delete(group.groupId);
      setBusy((n) => Math.max(0, n - 1));
    }
  }, [convert]);

  // Idle timer elapsed with the pen up: seal the burst; every handwriting-like cluster becomes its own group.
  const seal = useCallback(() => {
    const strokes = burstRef.current;
    burstRef.current = [];
    pickCandidates(strokes).forEach((cand) => recognize(cand, lastStrokeAtRef.current));
  }, [recognize]);

  const armTimer = () => { clearTimer(); timerRef.current = setTimeout(seal, INK_IDLE_MS); };

  // Pen down: never seal while a stroke is being drawn.
  const noteStrokeStart = useCallback(() => { if (enabled) clearTimer(); }, [enabled]);

  // Pen up (stroke committed): join the open burst, or pull back an in-flight group this stroke continues.
  const noteStroke = useCallback((item) => {
    if (!enabled || item.type !== 'stroke') return;
    const present = new Set(itemsRef.current);
    for (const [gid, p] of pendingRef.current) {
      if (strokeContinuesGroup(p.box, item) && p.strokes.every((s) => present.has(s) || s === item)) {
        p.ctrl.abort();
        pendingRef.current.delete(gid);
        burstRef.current = [...burstRef.current, ...p.strokes.filter((s) => !burstRef.current.includes(s))];
      }
    }
    burstRef.current = [...burstRef.current, item];
    lastStrokeAtRef.current = now();
    setSuggestion(null);
    armTimer();
  }, [enabled, seal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Undo / clear / removals: drop vanished strokes from the open burst and any stale suggestion.
  useEffect(() => {
    const present = new Set(items);
    if (burstRef.current.length) {
      const kept = burstRef.current.filter((s) => present.has(s));
      if (kept.length !== burstRef.current.length) { burstRef.current = kept; if (!kept.length) clearTimer(); }
    }
    setSuggestion((s) => (s && s.strokes.some((st) => !present.has(st)) ? null : s));
  }, [items]);

  useEffect(() => { if (!enabled || tool !== 'pen') reset(); }, [enabled, tool, reset]);
  useEffect(() => () => { clearTimer(); abortAll(); }, []);

  const accept = useCallback((textOverride) => {
    if (!suggestion) return;
    const text = (textOverride ?? suggestion.text).trim();
    if (text) convert(suggestion, text);
    setSuggestion(null);
  }, [suggestion, convert]);

  const dismiss = useCallback(() => setSuggestion(null), []);

  return { suggestion, flashTextId: flash?.id || null, busy: busy > 0, accept, dismiss, noteStroke, noteStrokeStart };
};
